import {
    CommonMancalaGame,
    GAME_STEP_BOARD_CLEARED,
    GAME_STEP_LAST_STONE_IN_BANK,
} from "mancala.js";
import { CaptureKind, getCapturedStones, getCaptureKind, getSowingPath } from "../history/MoveHistory";

const PLAYER1 = "player1";
const PLAYER2 = "player2";
const PLAYER1_STORE = 6;
const PLAYER2_STORE = 13;

// Where a move's last stone landed, from the mover's point of view.
export type Landing = "ownStore" | "ownPit" | "opponentPit";

// The immediate effect of playing a pit, worked out by the engine (docs/specs/tutorial).
export interface MoveOutcome {
    // Board index that was played; 0-5 for player 1, 7-12 for player 2.
    pitIndex: number;
    player1Moved: boolean;
    // Board indexes the stones were dropped into, in order.
    path: number[];
    landing: Landing;
    // Stones in the landing pit before the last stone arrived.
    stonesBeforeLanding: number;
    extraTurn: boolean;
    capture?: CaptureKind;
    // Stones taken from the opponent's pit by a capture.
    captured: number;
    // Stones the mover's store gained.
    storeGain: number;
    endsGame: boolean;
    // Stones in every pit after the move.
    board: number[];
}

export const isPlayer1Pit = (index: number) => index >= 0 && index < PLAYER1_STORE;
export const isPlayer2Pit = (index: number) => index > PLAYER1_STORE && index < PLAYER2_STORE;

// Plays the pit on a copy of the board with the real rules. Throws for a store or an empty pit.
export function getMoveOutcome(board: number[], pitIndex: number): MoveOutcome {
    const player1Moved = isPlayer1Pit(pitIndex);
    if (!player1Moved && !isPlayer2Pit(pitIndex)) throw new Error(`Not a pit: ${pitIndex}`);
    if (!(board[pitIndex] > 0)) throw new Error(`Pit ${pitIndex} is empty`);
    const game = new CommonMancalaGame("move-outcome", PLAYER1, PLAYER2);
    board.forEach((stoneCount, index) => (game.board.pits[index].stoneCount = stoneCount));
    const mover = player1Moved ? PLAYER1 : PLAYER2;
    game.turnPlayerId = mover;
    game.moveByPlayerPit(mover, player1Moved ? pitIndex : pitIndex - PLAYER1_STORE - 1);
    const gameSteps = game.history[game.history.length - 1].gameSteps;
    const after = game.board.getStoneArray();
    const path = getSowingPath(board, pitIndex);
    const last = path[path.length - 1];
    const ownStore = player1Moved ? PLAYER1_STORE : PLAYER2_STORE;
    const isOwnPit = player1Moved ? isPlayer1Pit : isPlayer2Pit;
    const dropsBeforeLast = path.slice(0, -1).filter((i) => i === last).length;
    return {
        pitIndex,
        player1Moved,
        path,
        landing: last === ownStore ? "ownStore" : isOwnPit(last) ? "ownPit" : "opponentPit",
        stonesBeforeLanding: (last === pitIndex ? 0 : board[last]) + dropsBeforeLast,
        extraTurn: gameSteps.some((step) => step.type === GAME_STEP_LAST_STONE_IN_BANK),
        capture: getCaptureKind(gameSteps),
        captured: getCapturedStones(board, pitIndex, gameSteps),
        storeGain: after[ownStore] - board[ownStore],
        endsGame: gameSteps.some((step) => step.type === GAME_STEP_BOARD_CLEARED),
        board: after,
    };
}

export type LessonTask = "anyMove" | "extraTurn" | "evenCapture" | "emptyPitCapture" | "endGame";

export function fulfilsTask(task: LessonTask, outcome: MoveOutcome): boolean {
    switch (task) {
        case "anyMove": return true;
        case "extraTurn": return outcome.extraTurn;
        case "evenCapture": return outcome.capture === "even";
        case "emptyPitCapture": return outcome.capture === "emptyPit";
        case "endGame": return outcome.endsGame;
    }
}

export type Mover = "you" | "bot";

type Translate = (key: string, options?: Record<string, unknown>) => string;

// What happened in a move and why, in one or two sentences (lessons and hints).
export function explainMove(outcome: MoveOutcome, mover: Mover, t: Translate): string {
    const who = mover === "you" ? "You" : "Bot";
    const sentences: string[] = [];
    if (outcome.capture === "even") {
        sentences.push(t(`MoveExplainEven${who}`, { count: outcome.captured }));
    } else if (outcome.capture === "emptyPit") {
        sentences.push(t(`MoveExplainEmptyPit${who}`, { count: outcome.captured }));
    } else if (outcome.landing === "ownStore") {
        // A move that ends the game gives no turn to play.
        if (!outcome.endsGame) sentences.push(t(`MoveExplainExtraTurn${who}`));
    } else if (outcome.landing === "opponentPit") {
        sentences.push(t(`MoveExplainOdd${who}`, { count: outcome.stonesBeforeLanding + 1 }));
    } else if (outcome.stonesBeforeLanding === 0) {
        sentences.push(t(`MoveExplainEmptyOpposite${who}`));
    } else {
        sentences.push(t(`MoveExplainOwnPit${who}`));
    }
    if (outcome.endsGame) sentences.push(t("MoveExplainGameOver"));
    return sentences.join(" ");
}
